import type { Field } from '@puckeditor/core'

/**
 * Marks Puck field definitions whose value a page writes into CSS text (a
 * `<style>` element), with the kind of value they must hold. The editor's
 * agent refuses a value that is not valid for its kind (agent/cssFields.ts);
 * the render side checks and escapes the same values for everyone
 * (utils/cssValues.ts).
 *
 * Colour pickers (`type: 'colorPicker'`) and image uploads
 * (`type: 'fileUpload'`) are recognised by their type. Mark the rest: a
 * custom field rendering ColorField or FileUploadField, or a text field
 * holding a font name or URL. The mark lives on the field object itself, so
 * it follows the field wherever a config reuses it (inside object fields,
 * spread into another component).
 */

export type CssValueKind =
  | 'color'
  | 'url'
  | 'fontFamily'
  | 'fontWeight'
  | 'fontStyle'

const kinds = new WeakMap<object, CssValueKind>()

/**
 * Returns `field` unchanged, marked as holding a CSS value of `kind`. The
 * `Field` constraint keeps the field literal typed as a Puck field where it
 * is written (literal `type`, typed `render` props).
 */
export function cssValueField<F extends Field<any>>(
  kind: CssValueKind,
  field: F,
): F {
  kinds.set(field, kind)
  return field
}

/** The kind a field was marked with by cssValueField, if any. */
export const markedCssValueKind = (field: unknown): CssValueKind | undefined =>
  field && typeof field === 'object' ? kinds.get(field) : undefined
